import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/formatters'
import type { InsuranceApplication } from '@/models/insuranceApplication'
import { COVERAGE_TYPE_LABELS, CoverageType } from '@/models/insuranceCoverage'
import { ChangeCoveragesDialog } from './ChangeCoveragesDialog'
import { COVERAGE_ICONS } from './coverageIcons'
import { useSummaryEditor } from './summaryPopover'
import { SummaryBody, SummaryRow } from './SummaryPrimitives'

interface SelectedCoverage {
  type: string
  label: string
}

/**
 * Coverage types the customer asked for. `metadata.selected_coverages` is written by
 * InsuranceCoverageAction; before that step runs we fall back to the coverage types the
 * carriers actually priced, so the popover isn't empty mid-flow.
 */
export function selectedCoverages(application: InsuranceApplication): SelectedCoverage[] {
  const toEntry = (type: string): SelectedCoverage => ({
    type,
    label: COVERAGE_TYPE_LABELS[type as CoverageType] ?? type,
  })

  const selected = application.metadata?.selectedCoverages
  if (Array.isArray(selected) && selected.length > 0) {
    return selected.map((type) => toEntry(String(type)))
  }

  const priced = new Set<string>()
  application.indications.forEach((indication) =>
    indication.coverages.forEach((coverage) => priced.add(coverage.coverageType))
  )
  return [...priced].map(toEntry)
}

/** Kept for the existing test, which pins the label mapping and the fallback. */
export function selectedCoverageLabels(application: InsuranceApplication): string[] {
  return selectedCoverages(application).map((coverage) => coverage.label)
}

export function CoverageNeedsSummary({
  application,
  onSuccess,
  readonly,
}: {
  application: InsuranceApplication
  /** Refetches the application after a coverage change saves. */
  onSuccess: () => void
  /** Shows the coverages with no way to change them. */
  readonly?: boolean
}) {
  const coverages = selectedCoverages(application)
  const currentInsurance = application.metadata?.currentInsurance
  const editor = useSummaryEditor()

  return (
    <SummaryBody>
      <span className="text-ink-400 text-xs font-semibold tracking-wide uppercase">Coverages requested</span>
      {coverages.length === 0 ? (
        <p className="text-ink-400 text-sm">No coverages selected yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {coverages.map((coverage) => {
            const Icon = COVERAGE_ICONS[coverage.type as CoverageType]
            return (
              <li key={coverage.type} className="flex items-center gap-3">
                {Icon && <Icon className="text-ink-500 size-5 shrink-0" />}
                <span className="text-foreground text-sm">{coverage.label}</span>
              </li>
            )
          })}
        </ul>
      )}

      {!readonly && (
        <>
          <Button type="button" variant="outline" size="sm" className="mt-4 w-full" onClick={editor.open}>
            Change coverages
          </Button>
          <ChangeCoveragesDialog
            open={editor.isOpen}
            onOpenChange={editor.setOpen}
            orgId={application.orgId}
            selected={coverages.map((coverage) => coverage.type)}
            onChanged={onSuccess}
            menu={application.coverageMenu}
          />
        </>
      )}

      {currentInsurance && (
        <div className="border-ink-100 mt-3 border-t pt-3">
          <SummaryRow label="Current insurer" value={currentInsurance.insurer} />
          <SummaryRow
            label="Expires"
            value={currentInsurance.expirationDate ? formatDate(currentInsurance.expirationDate) : '-'}
          />
        </div>
      )}
    </SummaryBody>
  )
}
